import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import ConfirmationModal from "../components/ConfirmationModal";
import SearchableSelect from "../components/SearchableSelect";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import { TableActionColumnHeader } from "../components/TableShell";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { useClearInvalidFilterSelection } from "../shared/hooks/useClearInvalidFilterSelection";
import { useLatestRequestSeq } from "../shared/hooks/useLatestRequestSeq";
import { IconCalendar, IconApprove, IconCancel, IconLabel, IconPayment, IconReopen, RowActionIcons, iconBtnStyle } from '../shared/icons/IconLib';
import { formatPhoneDisplay, normalizePhoneDigits } from "../shared/utils/phoneFormat";
import {
  allowsManualPaymentStatus,
  getPaymentLinkUrl,
  isIntegratedPaid,
  isManualSettledPaymentStatus,
  isPaidEvent,
  isPaymentLinkPaid,
} from "../shared/utils/eventPaymentMode";
import EventPriceBadge from "../components/EventPriceBadge";
import {
  filterEventsByOpenClosedStatus,
  getEventUiPhase,
} from "../shared/utils/eventOpenClosedStatus";
import { formatEventFilterOptionLabel } from "../shared/utils/eventFilterOptionLabel";
import {
  FILTER_SELECTION_ALL,
  filterRowsBySelectedForeignKey,
} from "../shared/utils/filterSelectionValidity";
import { createEventRegistrationDefaultSort } from "../shared/utils/eventParticipantSort";
import { EVENT_ATTENDANCE_FILTER_OPTIONS } from "../shared/utils/eventAttendanceOptions";
import EventStatusFilterDropdown from "../components/EventStatusFilterDropdown";

import { apiFetch } from "../shared/api/apiFetch";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { PAGE_VIEW_PREFERENCE_KEYS } from "../shared/api/pagePreferences";
import { usePageViewPreferences } from "../shared/hooks/usePageViewPreferences";
import PageViewPreferenceSaveButton from "../components/PageViewPreferenceSaveButton";
import { hasSearchParamContext } from "../shared/utils/pageViewPreferenceState";
import {
  applyEventSignupsPagePreferenceState,
  collectEventSignupsPagePreferenceState,
  getDefaultEventSignupsPreferenceResetPatch,
} from "../shared/utils/eventsListPagePreference";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const { fetchTablePreferences, saveTablePreferences } = require('../shared/api/tablePreferences');
const { displayEmailOrDash } = require('../shared/utils/displayEmail');
const ErrorBanner = require('../components/ErrorBanner').default;
const EventDetailModal = require('../components/EventDetailModal').default;
const ProfileInfoModal = require('../components/ProfileInfoModal').default;
const PhotographyConsentBadge = require('../components/PhotographyConsentBadge').default;
const { getPhotographyConsentSortKey, getPhotographyConsentLabel } = require('../shared/utils/photographyConsent');

const EVENT_PARTICIPANTS_TABLE_COLUMNS = [
  { id: 'event', label: 'Event' },
  { id: 'event_datetime', label: 'Event date/time' },
  { id: 'status', label: 'Status' },
  { id: 'program', label: 'Program' },
  { id: 'participant', label: 'Participant' },
  { id: 'email', label: 'Email ID' },
  { id: 'phone', label: 'Phone' },
  { id: 'parent_email', label: 'Parent email' },
  { id: 'parent_phone', label: 'Parent phone' },
  { id: 'registered_at', label: 'Registered' },
  { id: 'registration_type', label: 'Reg Type' },
  { id: 'price', label: 'Price ($)' },
  { id: 'payment', label: 'Payment' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'photo_consent', label: 'Photo consent' },
];
const EVENT_PARTICIPANTS_TABLE_DEFAULT_VISIBLE = [
  'event',
  'status',
  'program',
  'participant',
  'registered_at',
  'price',
  'payment',
  'attendance',
  'photo_consent',
  // hide by default
  // 'event_datetime',
  // 'email',
  // 'phone',
  // 'parent_email',
  // 'parent_phone',
  // 'registration_type',
];

function EventParticipants() {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paramEventId = (searchParams.get("eventId") || "").trim();
  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();
  const programFromUrl = (searchParams.get("program") || "").trim();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const beginParticipantsRequest = useLatestRequestSeq();

  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(() => paramEventId ? paramEventId : FILTER_SELECTION_ALL);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(() => createEventRegistrationDefaultSort('registration_datetime'));
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [markingPaymentStatus, setMarkingPaymentStatus] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [filterProgramId, setFilterProgramId] = useState(() =>
    programFromUrl && programFromUrl !== "__none__" ? programFromUrl : ""
  );
  const [detailEventId, setDetailEventId] = useState(null);
  const [profileParticipant, setProfileParticipant] = useState(null);
  const [collectingPaymentId, setCollectingPaymentId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    if (paramEventId) return new Set();
    const statusParam = (searchParams.get('status') || '').toLowerCase();
    if (statusParam === 'closed') return new Set(['Closed']);
    if (statusParam === 'expired') return new Set(['Expired']);
    if (statusParam === 'open') return new Set(['Open']);
    if (statusParam === 'all') return new Set();
    return new Set(['Open']);
  });
  const [viewMode, setViewMode] = useState(() => (searchParams.get('view') === 'card' ? 'card' : 'table'));
  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : "all",
    initialFocusDateYmd: navDateFromUrl || undefined,
  });
  const ATTENDANCE_OPTIONS = EVENT_ATTENDANCE_FILTER_OPTIONS;
  const [attendanceFilter, setAttendanceFilter] = useState(() => {
    const a = (searchParams.get('attendance') || '').trim();
    return ATTENDANCE_OPTIONS.includes(a) ? a : '';
  });
  const scopeClosedMetrics = (searchParams.get('scope') || '').toLowerCase() === 'closed_metrics';
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    onConfirm: null,
  });

  function getRegistrationType(participant, ev) {
    const existing = (participant?.registration_type != null ? String(participant.registration_type) : '').trim();
    if (existing) return existing;
    const reg = participant?.registration_date;
    const start = ev?.start_datetime;
    if (!reg || !start) return '';
    const regD = dayjs(reg);
    const startD = dayjs(start);
    if (!regD.isValid() || !startD.isValid()) return '';
    const dayDiff = startD.startOf('day').diff(regD.startOf('day'), 'day');
    if (dayDiff >= 1) return 'Registered';
    if (dayDiff === 0) return 'Walk-In';
    return '';
  }

  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState([...EVENT_PARTICIPANTS_TABLE_DEFAULT_VISIBLE]);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);

  const loadEvents = useCallback(() => {
    if (!account_id) {
      setEvents([]);
      setEventsLoaded(true);
      return;
    }
    setEventsLoaded(false);
    apiFetch(serverUrl + '/getEvent', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(err => {
        setBannerError(getMessageForFetchError(err));
        setEvents([]);
      })
      .finally(() => setEventsLoaded(true));
  }, [account_id, serverUrl]);

  const loadPrograms = useCallback(() => {
    if (!account_id) return;
    apiFetch(serverUrl + '/getProgram', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  }, [account_id, serverUrl]);

  const loadParticipants = useCallback(() => {
    if (!account_id) {
      setParticipants([]);
      setParticipantsLoading(false);
      return;
    }
    if (!selectedEventId || selectedEventId === '') {
      setParticipants([]);
      setParticipantsLoading(false);
      return;
    }
    const body = selectedEventId === FILTER_SELECTION_ALL
      ? { account_id }
      : { account_id, event_id: selectedEventId };
    const req = beginParticipantsRequest();
    setParticipantsLoading(true);
    apiFetch(serverUrl + '/getEventParticipantsWithEvents', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then(resp => resp.json())
      .then(data => {
        if (!req.isCurrent()) return;
        setParticipants(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!req.isCurrent()) return;
        setParticipants([]);
      })
      .finally(() => {
        if (!req.isCurrent()) return;
        setParticipantsLoading(false);
      });
  }, [account_id, serverUrl, selectedEventId, beginParticipantsRequest]);

  useEffect(() => {
    loadEvents();
    loadPrograms();
  }, [loadEvents, loadPrograms]);

  useEffect(() => {
    if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) timeNav.setView(navViewFromUrl);
    if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-driven initial navigator only
  }, []);

  // Sync Event filter from URL drill-in only. Do not reset to All when the user
  // picks an event from the dropdown (that previously wiped the selection).
  useEffect(() => {
    if (!paramEventId) return;
    if (!eventsLoaded) return;
    const exists = events.some(ev => String(ev._id) === String(paramEventId));
    const idStr = String(paramEventId);
    if (exists && idStr !== selectedEventId) {
      setSelectedEventId(idStr);
      setParticipants([]);
      setParticipantsLoading(true);
      // From drillto: apply only event filter, keep others as All
      setStatusFilter(new Set());
      setFilterProgramId('');
    }
  }, [paramEventId, events, eventsLoaded, selectedEventId]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  useEffect(() => {
    let cancelled = false;
    async function loadPrefs() {
      if (!account_id || !userInfo?._id) return;
      try {
        const ids = await fetchTablePreferences(account_id, userInfo._id, 'event_participants.table');
        if (cancelled) return;
        if (Array.isArray(ids) && ids.length > 0) {
          // Ensure ids are valid and keep at least one column visible.
          const allowed = new Set(EVENT_PARTICIPANTS_TABLE_COLUMNS.map(c => c.id));
          const filtered = ids.filter(id => allowed.has(id));
          if (filtered.length > 0) {
            const merged = filtered.includes('photo_consent') || !allowed.has('photo_consent')
              ? filtered
              : [...filtered, 'photo_consent'];
            setVisibleColumnIds(merged);
          }
        }
      } catch (err) {
        // Non-blocking; keep defaults.
      }
    }
    loadPrefs();
    return () => { cancelled = true; };
  }, [account_id, userInfo?._id]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const paymentSuccess = sp.get('payment_success') === '1';
    if (paymentSuccess && account_id) {
      loadParticipants();
      const cleanSearch = new URLSearchParams(location.search);
      cleanSearch.delete('payment_success');
      const newSearch = cleanSearch.toString();
      const newUrl = location.pathname + (newSearch ? '?' + newSearch : '');
      if (window.history.replaceState) window.history.replaceState(null, '', newUrl);
    }
  }, [account_id, location.pathname, location.search, loadParticipants]);

  function getEventForParticipant(participant) {
    if (participant?.event) return participant.event;
    if (!participant?.event_id) return null;
    return events.find(e => String(e._id) === String(participant.event_id)) || null;
  }

  function getParticipantDisplayName(a) {
    const name = [a.first_name, a.last_name].filter(Boolean).map(s => String(s).trim()).join(' ').trim();
    return name || '—';
  }

  function deleteParticipant(participantId) {
    setConfirmDialog({
      open: true,
      title: "Cancel registration",
      message: "Cancel this registration?",
      onConfirm: () => {
        apiFetch(serverUrl + '/deleteEventParticipant', {
          method: 'POST',
          body: JSON.stringify({ account_id, _id: participantId }),
        })
          .then(r => r.text())
          .then(text => {
            setConfirmDialog({
              open: true,
              title: "Cancel registration",
              message: text,
              mode: "alert",
              onConfirm: null,
            });
            loadParticipants();
            setSelectedIds(prev => { const s = new Set(prev); s.delete(participantId); return s; });
          })
          .catch(err => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function handleCollectPayment(participant, ev) {
    if (!isPaidEvent(ev)) {
      setConfirmDialog({
        open: true,
        title: "Collect payment",
        message: 'This event is not a paid event or has no price set.',
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    if ((participant.payment_status || '') !== 'Pending') {
      setConfirmDialog({
        open: true,
        title: "Collect payment",
        message: 'Payment is not pending for this participant.',
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    if (isPaymentLinkPaid(ev)) {
      const url = getPaymentLinkUrl(ev);
      if (!url) {
        setConfirmDialog({
          open: true,
          title: "Collect payment",
          message: 'Payment link URL is missing for this event.',
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!isIntegratedPaid(ev)) {
      setConfirmDialog({
        open: true,
        title: "Collect payment",
        message: 'Set this Paid event to Payment Link mode with a URL (or Integrated Checkout) before collecting payment.',
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setCollectingPaymentId(participant._id);
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const returnPath = location.pathname + (location.search || '');
    const successUrl = `${baseUrl}${returnPath}?payment_success=1`;
    const cancelUrl = `${baseUrl}${returnPath}`;
    apiFetch(serverUrl + '/createPaymentCheckoutSession', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        amount: Number(ev.price_cents) / 100,
        workflow_type: 'event_registration',
        workflow_ref: participant._id,
          customer_email: (participant.email || '').trim() || undefined,
        success_url: successUrl,
        cancel_url: cancelUrl,
        description: ev.name || 'Event registration',
      }),
    })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        if (data.checkout_url) {
          window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
          return;
        }
        setBannerError(data.error || 'Could not create payment link.');
      })
      .catch(err => setBannerError(getMessageForFetchError(err)))
      .finally(() => setCollectingPaymentId(null));
  }

  function handleSetPaymentStatus(participant, ev, nextStatus) {
    if (!allowsManualPaymentStatus(ev)) {
      setConfirmDialog({
        open: true,
        title: "Update payment",
        message: "Manual Paid/Sponsored/Pending is only available for Payment Link paid events.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    const label = String(nextStatus || "");
    setConfirmDialog({
      open: true,
      title: `Mark as ${label}`,
      message: `Set payment status to ${label} for ${getParticipantDisplayName(participant)}?`,
      mode: "confirm",
      onConfirm: () => {
        return apiFetch(serverUrl + '/updateEventParticipantPaymentStatus', {
          method: 'POST',
          body: JSON.stringify({
            account_id,
            _id: participant._id,
            payment_status: nextStatus,
          }),
        })
          .then(async (r) => {
            const text = await r.text();
            if (!r.ok) throw new Error(text || 'Update failed');
            setConfirmDialog({ open: false, title: '', message: '', mode: 'alert', onConfirm: null });
            loadParticipants();
          });
      },
    });
  }

  function handleSort(key) {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  }

  const eventsForEventFilter = useMemo(
    () => filterEventsByOpenClosedStatus(events, statusFilter),
    [events, statusFilter]
  );

  const clearSelectedIds = useCallback(() => setSelectedIds(new Set()), []);
  useClearInvalidFilterSelection({
    selectedId: selectedEventId,
    setSelectedId: setSelectedEventId,
    optionList: eventsForEventFilter,
    optionsReady: eventsLoaded,
    preserveSelectedId: paramEventId,
    allValue: FILTER_SELECTION_ALL,
    onClear: clearSelectedIds,
  });

  function getFilteredAndSortedData() {
    const getEventNameForParticipant = (a) => (events.find(ev => String(ev._id) === String(a.event_id))?.name || '').toLowerCase();
    let filtered = filterRowsBySelectedForeignKey(participants, 'event_id', selectedEventId);
    // Time range (same semantics as Event Management: overlap for day/week/month; start ≥ range for Future)
    if (timeNav.view !== "all") {
      const bounds = timeNav.bounds;
      const rangeStart = bounds.startInclusive ? dayjs(bounds.startInclusive) : null;
      const rangeEnd = bounds.endInclusive ? dayjs(bounds.endInclusive) : null;
      filtered = filtered.filter((a) => {
        const ev = getEventForParticipant(a);
        if (!ev) return false;
        const start = ev.start_datetime ? dayjs(ev.start_datetime) : null;
        const end = ev.end_datetime ? dayjs(ev.end_datetime) : start;
        const sStart = start && start.isValid() ? start : end && end.isValid() ? end : null;
        const sEnd = end && end.isValid() ? end : start && start.isValid() ? start : null;
        if (!sStart || !sEnd) return false;
        if (timeNav.view === "future") {
          if (rangeStart && sStart.isBefore(rangeStart)) return false;
          return true;
        }
        if (rangeStart && sEnd.isBefore(rangeStart)) return false;
        if (rangeEnd && sStart.isAfter(rangeEnd)) return false;
        return true;
      });
    }
    // Filter by event's program, organizer, status
    if ((filterProgramId || '').trim()) {
      filtered = filtered.filter(a => {
        const ev = getEventForParticipant(a);
        return ev && String(ev.program_id || '') === String(filterProgramId).trim();
      });
    }
    if (statusFilter.size > 0) {
      filtered = filtered.filter(a => {
        const ev = getEventForParticipant(a);
        return statusFilter.has(getEventUiPhase(ev));
      });
    }
    // Attendance filter (e.g. from Closed Events Metrics drill)
    if ((attendanceFilter || '').trim()) {
      const wantAtt = attendanceFilter.trim().toLowerCase();
      filtered = filtered.filter(a => (a.attendance || 'Registered').trim().toLowerCase() === wantAtt);
    }
    // Scope: closed metrics = only participants from closed events in last 12 months (so drill-to count matches chart)
    if (scopeClosedMetrics) {
      const twelveMonthsAgo = dayjs().startOf('day').subtract(12, 'month');
      filtered = filtered.filter(a => {
        const ev = getEventForParticipant(a);
        if (!ev) return false;
        const dt = ev.end_datetime || ev.start_datetime;
        if (!dt || !dayjs(dt).isValid()) return false;
        return !dayjs(dt).isBefore(twelveMonthsAgo);
      });
    }
    // Search filter
    filtered = filtered.filter(a => {
      const searchLower = (searchTerm || '').trim().toLowerCase();
      if (!searchLower) return true;
      return (
        (a.first_name && a.first_name.toLowerCase().includes(searchLower)) ||
        (a.last_name && a.last_name.toLowerCase().includes(searchLower)) ||
        (a.email && a.email.toLowerCase().includes(searchLower)) ||
        (a.phone && String(a.phone).toLowerCase().includes(searchLower)) ||
        getEventNameForParticipant(a).includes(searchLower) ||
        getPhotographyConsentLabel(a.photography_consent).toLowerCase().includes(searchLower)
      );
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const evA = getEventForParticipant(a);
        const evB = getEventForParticipant(b);
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'event_name') {
          aVal = evA?.name ?? '';
          bVal = evB?.name ?? '';
        } else if (sortConfig.key === 'event_status') {
          aVal = getEventUiPhase(evA).toLowerCase();
          bVal = getEventUiPhase(evB).toLowerCase();
        } else if (sortConfig.key === 'event_program') {
          aVal = (evA?.program_name != null && evA.program_name !== '') ? evA.program_name : '';
          bVal = (evB?.program_name != null && evB.program_name !== '') ? evB.program_name : '';
        } else if (sortConfig.key === 'participant_name') {
          aVal = getParticipantDisplayName(a).toLowerCase();
          bVal = getParticipantDisplayName(b).toLowerCase();
        } else if (sortConfig.key === 'event_datetime') {
          aVal = evA?.start_datetime && dayjs(evA.start_datetime).isValid()
            ? dayjs(evA.start_datetime).valueOf()
            : 0;
          bVal = evB?.start_datetime && dayjs(evB.start_datetime).isValid()
            ? dayjs(evB.start_datetime).valueOf()
            : 0;
        } else if (sortConfig.key === 'email') {
          aVal = displayEmailOrDash(a.email).toLowerCase();
          bVal = displayEmailOrDash(b.email).toLowerCase();
        } else if (sortConfig.key === 'phone') {
          aVal = normalizePhoneDigits(a.phone || '');
          bVal = normalizePhoneDigits(b.phone || '');
        } else if (sortConfig.key === 'parent_email') {
          aVal = displayEmailOrDash(a.parent_email).toLowerCase();
          bVal = displayEmailOrDash(b.parent_email).toLowerCase();
        } else if (sortConfig.key === 'parent_phone') {
          aVal = normalizePhoneDigits(a.parent_phone || '');
          bVal = normalizePhoneDigits(b.parent_phone || '');
        } else if (sortConfig.key === 'registration_datetime') {
          aVal = a?.registration_date ? new Date(a.registration_date).getTime() : 0;
          bVal = b?.registration_date ? new Date(b.registration_date).getTime() : 0;
        } else if (sortConfig.key === 'registration_type') {
          aVal = (getRegistrationType(a, evA) || '').toLowerCase();
          bVal = (getRegistrationType(b, evB) || '').toLowerCase();
        } else if (sortConfig.key === 'attendance') {
          aVal = (a.attendance || 'Registered').toLowerCase();
          bVal = (b.attendance || 'Registered').toLowerCase();
        } else if (sortConfig.key === 'payment_status') {
          aVal = (a.payment_status || 'NA').toLowerCase();
          bVal = (b.payment_status || 'NA').toLowerCase();
        } else if (sortConfig.key === 'event_price') {
          aVal = (evA?.event_type === 'Paid' && evA?.price_cents != null) ? Number(evA.price_cents) : -1;
          bVal = (evB?.event_type === 'Paid' && evB?.price_cents != null) ? Number(evB.price_cents) : -1;
        } else if (sortConfig.key === 'photo_consent') {
          aVal = getPhotographyConsentSortKey(a.photography_consent);
          bVal = getPhotographyConsentSortKey(b.photography_consent);
        }
        if (aVal !== undefined && bVal !== undefined && typeof aVal !== 'string' && typeof bVal !== 'string') {
          if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        aVal = aVal ?? '';
        bVal = bVal ?? '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function handleSelectAll(e) {
    const data = getFilteredAndSortedData();
    const next = new Set(selectedIds);
    if (e.target.checked) data.forEach(a => next.add(a._id));
    else data.forEach(a => next.delete(a._id));
    setSelectedIds(next);
  }

  function handleSelectOne(id, checked) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Cancel registration(s)",
        message: 'Please select at least one participant to cancel',
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Cancel registration(s)",
      message: `Cancel ${selectedVisibleIds.size} registration(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const ids = Array.from(selectedVisibleIds);
        let success = 0, fail = 0;
        const errors = [];
        for (const _id of ids) {
          try {
            const res = await apiFetch(serverUrl + '/deleteEventParticipant', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id }),
            });
            const text = await res.text();
            if (res.ok) success++;
            else { fail++; errors.push(`${_id}: ${text}`); }
          } catch (err) {
            fail++;
            errors.push(`${_id}: ${getMessageForFetchError(err)}`);
          }
        }
        let msg = `Cancellation completed. Success: ${success}, Errors: ${fail}`;
        if (errors.length > 0 && errors.length <= 10) msg += '\n\n' + errors.join('\n');
        else if (errors.length > 10) msg += '\n\n' + errors.slice(0, 10).join('\n');
        setConfirmDialog({
          open: true,
          title: "Cancel registration(s)",
          message: msg,
          mode: "alert",
          onConfirm: null,
        });
        setSelectedIds(new Set());
        loadParticipants();
        setDeleting(false);
      },
    });
  }

  function getSelectedPendingPaymentLinkRows() {
    const selectedVisibleIds = getSelectedVisibleIds();
    return getFilteredAndSortedData().filter((participant) => {
      if (!selectedVisibleIds.has(participant._id)) return false;
      const event = getEventForParticipant(participant);
      return (
        (participant.payment_status || '') === 'Pending' &&
        allowsManualPaymentStatus(event)
      );
    });
  }

  function markSelectedPaymentStatus(nextStatus) {
    const label = nextStatus === "Sponsored" ? "Sponsored" : "Paid";
    const eligibleRows = getSelectedPendingPaymentLinkRows();
    if (eligibleRows.length === 0) {
      setConfirmDialog({
        open: true,
        title: `Mark selected as ${label}`,
        message: "Select at least one Pending participant from a Payment Link paid event.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    const unchangedCount = getSelectedVisibleIds().size - eligibleRows.length;
    setConfirmDialog({
      open: true,
      title: `Mark selected as ${label}`,
      message:
        `Mark ${eligibleRows.length} selected payment(s) as ${label}?` +
        (unchangedCount > 0
          ? `\n\n${unchangedCount} other selected row(s) are not Pending Payment Link registrations and will remain unchanged.`
          : ""),
      mode: "confirm",
      onConfirm: async () => {
        setMarkingPaymentStatus(true);
        try {
          const ids = eligibleRows.map((row) => row._id);
          const response = await apiFetch(serverUrl + '/updateEventParticipantPaymentStatusBulk', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              _ids: ids,
              payment_status: nextStatus,
            }),
          });
          const text = await response.text();
          if (!response.ok) throw new Error(text || 'Bulk payment update failed.');
          setSelectedIds((previous) => {
            const next = new Set(previous);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          await loadParticipants();
          setConfirmDialog({
            open: true,
            title: "Payments updated",
            message: `Marked ${eligibleRows.length} payment(s) as ${label}.`,
            mode: "alert",
            onConfirm: null,
          });
        } catch (err) {
          setBannerError(getMessageForFetchError(err));
        } finally {
          setMarkingPaymentStatus(false);
        }
      },
    });
  }

  const hasEventSelected = selectedEventId && selectedEventId !== '';
  const selectedPendingPaymentLinkCount = getSelectedPendingPaymentLinkRows().length;
  const selectedVisibleCount = getSelectedVisibleIds().size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  const shouldSkipSignupsPagePreferenceLoad = useCallback(
    () =>
      hasSearchParamContext(searchParams, [
        "eventId",
        "attendance",
        "status",
        "scope",
        "program",
        "navView",
        "navDate",
        "view",
      ]),
    [searchParams]
  );

  const collectSignupsPageViewState = useCallback(
    () =>
      collectEventSignupsPagePreferenceState({
        searchTerm,
        statusFilter,
        viewMode,
        navView: timeNav.view,
        navDate: timeNav.focusDateYmd,
        filterProgramId,
        attendanceFilter,
      }),
    [
      searchTerm,
      statusFilter,
      viewMode,
      timeNav.view,
      timeNav.focusDateYmd,
      filterProgramId,
      attendanceFilter,
    ]
  );

  const applySignupsPageViewState = useCallback(
    (state) => {
      applyEventSignupsPagePreferenceState(state, {
        setSearchTerm,
        setStatusFilter,
        setViewMode,
        setNavView: timeNav.setView,
        setNavDate: timeNav.setFocusDateYmd,
        setFilterProgramId,
        setAttendanceFilter,
      });
    },
    [timeNav.setView, timeNav.setFocusDateYmd]
  );

  const clearSignupsPageFiltersAfterDefaultCleared = useCallback(() => {
    const patch = getDefaultEventSignupsPreferenceResetPatch();
    setSearchTerm(patch.searchTerm);
    setStatusFilter(patch.statusFilter);
    setViewMode(patch.viewMode);
    timeNav.setView(patch.navView);
    timeNav.setFocusDateYmd(patch.navDate);
    setFilterProgramId(patch.filterProgramId);
    setAttendanceFilter(patch.attendanceFilter);
  }, [timeNav]);

  const confirmClearSignupsDefaultView = useCallback((proceed) => {
    setConfirmDialog({
      open: true,
      title: "Reset default view",
      message: "Clear your saved Event Sign-ups default view and restore the standard filters?",
      mode: "confirm",
      onConfirm: () => {
        setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
        if (typeof proceed === "function") proceed();
      },
    });
  }, []);

  const signupsPageViewPreferenceHookConfig = useMemo(
    () => ({
      pageKey: PAGE_VIEW_PREFERENCE_KEYS.eventSignups,
      accountId: account_id,
      userId: userInfo?._id,
      enabled: Boolean(account_id && userInfo?._id),
      shouldSkipLoad: shouldSkipSignupsPagePreferenceLoad,
      collectState: collectSignupsPageViewState,
      applyState: applySignupsPageViewState,
    }),
    [
      account_id,
      userInfo?._id,
      shouldSkipSignupsPagePreferenceLoad,
      collectSignupsPageViewState,
      applySignupsPageViewState,
    ]
  );

  const signupsPageViewPreferences = usePageViewPreferences(signupsPageViewPreferenceHookConfig);

  function renderParticipantRowActions(participant, ev, { variant = "menu" } = {}) {
    const paymentStatus = participant.payment_status || '';
    const canCollectPayment =
      paymentStatus === 'Pending'
      && (isPaymentLinkPaid(ev) || isIntegratedPaid(ev));
    const canManualPayment = allowsManualPaymentStatus(ev);
    const isSettled = isManualSettledPaymentStatus(paymentStatus);
    return (
      <RowActionIcons variant={variant}>
        {canCollectPayment && (
          <button
            type="button"
            onClick={() => handleCollectPayment(participant, ev)}
            disabled={collectingPaymentId === participant._id}
            className="btn-link"
            style={iconBtnStyle}
            title={isPaymentLinkPaid(ev) ? "Open Payment Link" : "Open Stripe Checkout to collect payment"}
          >
            {collectingPaymentId === participant._id ? 'Opening…' : <IconPayment size={18} />}
          </button>
        )}
        {canManualPayment && paymentStatus === 'Pending' && (
          <button
            type="button"
            onClick={() => handleSetPaymentStatus(participant, ev, 'Paid')}
            className="btn-link"
            style={iconBtnStyle}
            title="Mark as Paid"
          >
            <IconApprove size={18} />
          </button>
        )}
        {canManualPayment && paymentStatus === 'Pending' && (
          <button
            type="button"
            onClick={() => handleSetPaymentStatus(participant, ev, 'Sponsored')}
            className="btn-link"
            style={iconBtnStyle}
            title="Mark as Sponsored"
          >
            <IconLabel size={18} />
          </button>
        )}
        {canManualPayment && paymentStatus === 'Paid' && (
          <button
            type="button"
            onClick={() => handleSetPaymentStatus(participant, ev, 'Sponsored')}
            className="btn-link"
            style={iconBtnStyle}
            title="Mark as Sponsored"
          >
            <IconLabel size={18} />
          </button>
        )}
        {canManualPayment && paymentStatus === 'Sponsored' && (
          <button
            type="button"
            onClick={() => handleSetPaymentStatus(participant, ev, 'Paid')}
            className="btn-link"
            style={iconBtnStyle}
            title="Mark as Paid"
          >
            <IconApprove size={18} />
          </button>
        )}
        {canManualPayment && isSettled && (
          <button
            type="button"
            onClick={() => handleSetPaymentStatus(participant, ev, 'Pending')}
            className="btn-link"
            style={iconBtnStyle}
            title="Mark as Pending"
          >
            <IconReopen size={18} />
          </button>
        )}
        {participant.attendance === 'Cancelled' ? null : (
          <button
            type="button"
            onClick={() => deleteParticipant(participant._id)}
            className="btn-link-danger"
            style={iconBtnStyle}
            title="Cancel registration"
          >
            <IconCancel size={18} />
          </button>
        )}
      </RowActionIcons>
    );
  }

  return (
    <div className="page-content">
        <div className="page-top">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />

          <ColumnCustomizerModal
            open={columnModalOpen}
            onClose={() => setColumnModalOpen(false)}
            allColumns={EVENT_PARTICIPANTS_TABLE_COLUMNS}
            visibleIds={visibleColumnIds}
            onChangeVisibleIds={setVisibleColumnIds}
            saving={columnPrefsSaving}
            onReset={async () => {
              const defaultIds = [...EVENT_PARTICIPANTS_TABLE_DEFAULT_VISIBLE];
              setVisibleColumnIds(defaultIds);
              if (!account_id || !userInfo?._id) {
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, userInfo._id, 'event_participants.table', defaultIds);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: 'Event Sign-ups columns',
                  message: err && err.message ? err.message : 'Failed to reset column preferences',
                  mode: 'alert',
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
              setColumnModalOpen(false);
            }}
            onSave={async (ids) => {
              if (!account_id || !userInfo?._id) {
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, userInfo._id, 'event_participants.table', ids);
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: 'Event Sign-ups columns',
                  message: err && err.message ? err.message : 'Failed to save column preferences',
                  mode: 'alert',
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
            }}
          />

          <ConfirmationModal
            open={confirmDialog.open}
            title={confirmDialog.title || (confirmDialog.mode === "alert" ? "Message" : "Confirm")}
            message={confirmDialog.message}
            mode={confirmDialog.mode || "confirm"}
            onConfirm={async () => {
              const fn = confirmDialog.onConfirm;
              setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
              if (fn) await fn();
            }}
            onCancel={() =>
              setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
            }
          />
          <div className="toolbar">
            <span className="toolbar-label">Event</span>
            <div className="input-min-200" style={{ minWidth: 200 }}>
              <SearchableSelect
                value={selectedEventId}
                onChange={(v) => {
                  const nextId = String(v || FILTER_SELECTION_ALL);
                  setSelectedEventId(nextId);
                  setSelectedIds(new Set());
                }}
                options={eventsForEventFilter.map((e) => ({
                  value: String(e._id),
                  label: formatEventFilterOptionLabel(e),
                }))}
                placeholder="— All —"
                searchPlaceholder="Search event…"
                allowEmpty={true}
                emptyLabel="— All —"
                maxVisibleItems={5}
                dropdownPortal={true}
                title="Event filter"
              />
            </div>
            <span className="toolbar-label">Program</span>
            <div className="input-min-160" style={{ minWidth: 160 }}>
              <SearchableSelect
                value={filterProgramId}
                onChange={(v) => setFilterProgramId(String(v || ""))}
                options={(programs || []).map((g) => ({ value: String(g._id), label: String(g?.name || "Program") }))}
                placeholder="— All —"
                searchPlaceholder="Search program…"
                allowEmpty={true}
                emptyLabel="— All —"
                maxVisibleItems={4}
                dropdownPortal={true}
                title="Program filter"
              />
            </div>
            <EventStatusFilterDropdown value={statusFilter} onChange={setStatusFilter} />
            <span className="toolbar-label">Attendance:</span>
            <select value={attendanceFilter} onChange={(e) => setAttendanceFilter(e.target.value)} className="input select input-min-130">
              <option value="">— All —</option>
              {ATTENDANCE_OPTIONS.filter(x => x !== '').map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="toolbar">
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search participant name, email, phone, event…" className="input input-search-wide" />
            <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
            {hasEventSelected && viewMode === 'table' && (
              <>
                <span className="toolbar-divider" />
                <button
                  type="button"
                  onClick={() => markSelectedPaymentStatus('Paid')}
                  disabled={selectedPendingPaymentLinkCount === 0 || markingPaymentStatus || deleting}
                  className="btn btn-primary"
                  title={
                    selectedPendingPaymentLinkCount === 0
                      ? "Select Pending Payment Link registrations"
                      : `Mark ${selectedPendingPaymentLinkCount} selected payment(s) as Paid`
                  }
                >
                  {markingPaymentStatus
                    ? 'Updating…'
                    : `Mark paid${selectedPendingPaymentLinkCount > 0 ? ` (${selectedPendingPaymentLinkCount})` : ''}`}
                </button>
                <button
                  type="button"
                  onClick={() => markSelectedPaymentStatus('Sponsored')}
                  disabled={selectedPendingPaymentLinkCount === 0 || markingPaymentStatus || deleting}
                  className="btn btn-secondary"
                  title={
                    selectedPendingPaymentLinkCount === 0
                      ? "Select Pending Payment Link registrations"
                      : `Mark ${selectedPendingPaymentLinkCount} selected payment(s) as Sponsored`
                  }
                >
                  {markingPaymentStatus
                    ? 'Updating…'
                    : `Mark sponsored${selectedPendingPaymentLinkCount > 0 ? ` (${selectedPendingPaymentLinkCount})` : ''}`}
                </button>
                <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Cancel{selectedCountSuffix}</button>
              </>
            )}
            <span className="toolbar-divider" />
            <ViewModeIconToggle
              value={viewMode}
              onChange={setViewMode}
              modes={["table", "card"]}
              ariaLabel="View"
            />
            <PageViewPreferenceSaveButton
              {...signupsPageViewPreferenceHookConfig}
              preferencesControl={signupsPageViewPreferences}
              onAfterClearLocalState={clearSignupsPageFiltersAfterDefaultCleared}
              onClearConfirm={confirmClearSignupsDefaultView}
              onError={(message) =>
                setConfirmDialog({
                  open: true,
                  title: "Default view",
                  message,
                  mode: "alert",
                  onConfirm: null,
                })
              }
            />
            <span className="toolbar-divider" />
            <GlobalTimeNavigatorBar
              variant="inline"
              title={timeNav.title}
              view={timeNav.view}
              onViewChange={timeNav.setView}
              onToday={timeNav.goToday}
              onPrev={timeNav.movePrev}
              onNext={timeNav.moveNext}
            />
          </div>
        </div>
        <div className="table-scroll">
        {!hasEventSelected ? (
          <p className="empty-message">
            {!paramEventId
              ? <>Use the filters above to narrow the list.{programs.length === 0 ? ' Loading…' : ''}</>
              : 'Event not found. Switch to the Events tab and select an event from the list.'
            }
          </p>
        ) : (
          <>
            {viewMode === 'card' && (
              participantsLoading ? (
                <p style={{ padding: '24px 10px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>Loading participants…</p>
              ) : getFilteredAndSortedData().length === 0 ? (
                <p style={{ padding: '12px', color: '#6c757d', fontSize: '13px' }}>
                  {participants.length === 0 ? 'No participants.' : 'No participants match your filters.'}
                </p>
              ) : (
                <div className="cards-wrapper">
                  {getFilteredAndSortedData().map((a) => {
                    const ev = getEventForParticipant(a);
                    const eventPhase = getEventUiPhase(ev);
                    const programDisplay = (ev?.program_name != null && ev.program_name !== '') ? ev.program_name : '';
                    const participantName = [a.first_name, a.last_name].filter(Boolean).join(' ') || '—';
                    return (
                      <article key={a._id} className="card EventCalendar-card">
                        <div className="EventCalendar-card-body">
                          <div className="EventCalendar-card-date">
                            <IconCalendar />
                            {ev?.start_datetime ? dayjs(ev.start_datetime).format('dddd, MMMM D, YYYY') : '—'}
                          </div>
                          <h3 className="EventCalendar-card-title">
                            <button
                              type="button"
                              onClick={() => setProfileParticipant(a)}
                              className="btn-link"
                              title="View profile info"
                            >
                              {participantName}
                            </button>
                          </h3>
                          {ev && (
                            <div className="EventCalendar-card-location" style={{ marginBottom: '4px' }}>
                              <button
                                type="button"
                                onClick={() => setDetailEventId(ev._id)}
                                className="btn-link"
                                style={{ width: '100%', textAlign: 'left', fontSize: '0.9rem' }}
                                title="View event details"
                              >
                                {ev.name}
                              </button>
                            </div>
                          )}
                          {ev?.start_datetime && ev?.end_datetime && (
                            <div className="EventCalendar-card-location" style={{ marginBottom: '4px' }}>
                              <span style={{ fontSize: '0.78rem', color: '#6c757d' }}>
                                {dayjs(ev.start_datetime).format('h:mm A')} – {dayjs(ev.end_datetime).format('h:mm A')}
                              </span>
                            </div>
                          )}
                          {(() => {
                            const emailDisp = displayEmailOrDash(a.email);
                            const phoneDisp = formatPhoneDisplay(a.phone);
                            const show = emailDisp !== "—" || !!normalizePhoneDigits(a.phone || "");
                            if (!show) return null;
                            return (
                              <div className="EventCalendar-card-location" style={{ marginBottom: '4px' }}>
                                <span>{[emailDisp, phoneDisp !== "—" ? phoneDisp : null].filter(Boolean).join(' · ')}</span>
                              </div>
                            );
                          })()}
                          {programDisplay && (
                            <div className="EventCalendar-card-location" style={{ marginBottom: '4px' }}>
                              <span>{programDisplay}</span>
                            </div>
                          )}
                          <div
                            className="EventCalendar-card-actions"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap',
                              gap: '8px',
                              overflow: 'hidden',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '0.75rem',
                                color: eventPhase === 'Closed' ? '#374151' : eventPhase === 'Expired' ? '#dc2626' : '#16a34a',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                flexShrink: 0,
                                background: eventPhase === 'Closed' ? '#f3f4f6' : eventPhase === 'Expired' ? '#fef2f2' : '#f0fdf4',
                              }}
                            >
                              {eventPhase}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#6c757d', flexShrink: 0 }}>
                              {a.attendance || 'Registered'}
                            </span>
                            <PhotographyConsentBadge consent={a.photography_consent} />
                            {(a.payment_status && a.payment_status !== 'NA') && (
                              <span style={{ fontSize: '0.75rem', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                Pay: {a.payment_status}
                              </span>
                            )}
                            {renderParticipantRowActions(a, ev, { variant: "inline" })}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )
            )}

            {viewMode === 'table' && (
            <>
            <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 40, minWidth: 40 }}><input type="checkbox" checked={getFilteredAndSortedData().length > 0 && getFilteredAndSortedData().every(a => selectedIds.has(a._id))} onChange={handleSelectAll} disabled={getFilteredAndSortedData().length === 0} /></th>
                  {visibleColumnIds.includes('event') && (
                    <th className="sortable" onClick={() => handleSort('event_name')}>
                      Event
                    </th>
                  )}
                  {visibleColumnIds.includes('event_datetime') && (
                    <th className="sortable" onClick={() => handleSort('event_datetime')} style={{ whiteSpace: 'nowrap' }}>
                      Event date/time
                    </th>
                  )}
                  {visibleColumnIds.includes('status') && (
                    <th className="sortable" onClick={() => handleSort('event_status')}>Status</th>
                  )}
                  {visibleColumnIds.includes('program') && (
                    <th className="sortable" onClick={() => handleSort('event_program')}>Program</th>
                  )}
                  {visibleColumnIds.includes('participant') && (
                    <th className="sortable" onClick={() => handleSort('participant_name')}>Participant</th>
                  )}
                  {visibleColumnIds.includes('email') && (
                    <th className="sortable" onClick={() => handleSort('email')}>Email ID</th>
                  )}
                  {visibleColumnIds.includes('phone') && (
                    <th className="sortable" onClick={() => handleSort('phone')}>Phone</th>
                  )}
                  {visibleColumnIds.includes('parent_email') && (
                    <th className="sortable" onClick={() => handleSort('parent_email')}>Parent email</th>
                  )}
                  {visibleColumnIds.includes('parent_phone') && (
                    <th className="sortable" onClick={() => handleSort('parent_phone')}>Parent phone</th>
                  )}
                  {visibleColumnIds.includes('registered_at') && (
                    <th className="sortable" onClick={() => handleSort('registration_datetime')}>Registered</th>
                  )}
                  {visibleColumnIds.includes('registration_type') && (
                    <th className="sortable" onClick={() => handleSort('registration_type')}>Reg Type</th>
                  )}
                  {visibleColumnIds.includes('price') && (
                    <th className="sortable" onClick={() => handleSort('event_price')}>Price ($)</th>
                  )}
                  {visibleColumnIds.includes('payment') && (
                    <th className="sortable" onClick={() => handleSort('payment_status')}>Payment</th>
                  )}
                  {visibleColumnIds.includes('attendance') && (
                    <th className="sortable" onClick={() => handleSort('attendance')}>Attendance</th>
                  )}
                  {visibleColumnIds.includes('photo_consent') && (
                    <th className="sortable" onClick={() => handleSort('photo_consent')}>Photo consent</th>
                  )}
                  <TableActionColumnHeader
                    align="left"
                    onCustomizeColumns={() => setColumnModalOpen(true)}
                    customizeDisabled={columnPrefsSaving}
                  />
                </tr>
              </thead>
              <tbody>
                {                participantsLoading ? (
                  <tr>
                    <td colSpan={2 + Math.max(1, (visibleColumnIds || []).length)} className="empty-message">Loading participants…</td>
                  </tr>
                ) : (
                getFilteredAndSortedData().map(a => {
                  const ev = getEventForParticipant(a);
                  const statusCell = getEventUiPhase(ev);
                  const programCell = (ev?.program_name != null && ev.program_name !== '') ? ev.program_name : '—';
                  const priceCell = <EventPriceBadge event={ev || {}} />;
                  const registeredAtCell = a?.registration_date && dayjs(a.registration_date).isValid()
                    ? dayjs(a.registration_date).format('MM/DD/YYYY h:mm A')
                    : '—';
                  const registrationTypeCell = getRegistrationType(a, ev) || '—';
                  const eventNameCell = ev?.name || '—';
                  const eventDateTimeCell =
                    ev?.start_datetime && dayjs(ev.start_datetime).isValid()
                      ? dayjs(ev.start_datetime).format('MM/DD/YYYY h:mm A')
                      : '—';
                  const emailCell = displayEmailOrDash(a.email);
                  const phoneCell = formatPhoneDisplay(a.phone);
                  const parentEmailCell = displayEmailOrDash(a.parent_email);
                  const parentPhoneCell = formatPhoneDisplay(a.parent_phone);
                  return (
                    <tr key={a._id}>
                      <td className="text-center" style={{ width: 40, minWidth: 40 }}>
                        <input type="checkbox" checked={selectedIds.has(a._id)} onChange={(e) => handleSelectOne(a._id, e.target.checked)} />
                      </td>
                      {visibleColumnIds.includes('event') && (
                        <td>
                          {ev ? (
                            <button type="button" onClick={() => setDetailEventId(ev._id)} className="btn-link" title={eventDateTimeCell !== '—' ? `${eventNameCell} — ${eventDateTimeCell}` : eventNameCell}>{eventNameCell}</button>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      {visibleColumnIds.includes('event_datetime') && (
                        <td style={{ whiteSpace: 'nowrap' }}>{eventDateTimeCell}</td>
                      )}
                      {visibleColumnIds.includes('status') && <td>{statusCell}</td>}
                      {visibleColumnIds.includes('program') && <td>{programCell}</td>}
                      {visibleColumnIds.includes('participant') && (
                        <td>
                          <button type="button" onClick={() => setProfileParticipant(a)} className="btn-link" title="View profile info">{getParticipantDisplayName(a)}</button>
                        </td>
                      )}
                      {visibleColumnIds.includes('email') && <td>{emailCell}</td>}
                      {visibleColumnIds.includes('phone') && <td style={{ whiteSpace: 'nowrap' }}>{phoneCell}</td>}
                      {visibleColumnIds.includes('parent_email') && <td>{parentEmailCell}</td>}
                      {visibleColumnIds.includes('parent_phone') && <td style={{ whiteSpace: 'nowrap' }}>{parentPhoneCell}</td>}
                      {visibleColumnIds.includes('registered_at') && <td style={{ whiteSpace: 'nowrap' }}>{registeredAtCell}</td>}
                      {visibleColumnIds.includes('registration_type') && <td style={{ whiteSpace: 'nowrap' }}>{registrationTypeCell}</td>}
                      {visibleColumnIds.includes('price') && <td>{priceCell}</td>}
                      {visibleColumnIds.includes('payment') && <td>{a.payment_status || 'NA'}</td>}
                      {visibleColumnIds.includes('attendance') && <td>{a.attendance || 'Registered'}</td>}
                      {visibleColumnIds.includes('photo_consent') && (
                        <td>
                          <PhotographyConsentBadge consent={a.photography_consent} />
                        </td>
                      )}
                      <td>
                        {renderParticipantRowActions(a, ev)}
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
            </div>
            {viewMode === 'table' && !participantsLoading && participants.length === 0 && <p className="empty-message">No participants.</p>}
            </>
            )}
          </>
        )}
      </div>
      {detailEventId && (
        <EventDetailModal
          eventId={detailEventId}
          open={!!detailEventId}
          onClose={() => setDetailEventId(null)}
          returnTo={location.pathname + (location.search || '')}
          eventsFilters={null}
        />
      )}
      <ProfileInfoModal
        open={!!profileParticipant}
        onClose={() => setProfileParticipant(null)}
        showAllRegistrationFields
        participantUserId={profileParticipant?.user_id}
        relatedEventId={profileParticipant?.event_id}
        firstName={profileParticipant?.first_name}
        lastName={profileParticipant?.last_name}
        preferredName={profileParticipant?.preferred_name}
        email={profileParticipant?.email}
        phone={profileParticipant?.phone}
        birthday={profileParticipant?.birthday}
        gender={profileParticipant?.gender}
        grade={profileParticipant?.grade}
        school={profileParticipant?.school}
        schoolDistrict={profileParticipant?.school_district}
        raceEthnicity={profileParticipant?.race_ethnicity}
        raceEthnicityOther={profileParticipant?.race_ethnicity_other}
        photographyConsent={profileParticipant?.photography_consent}
        parentName={profileParticipant?.parent_name}
        parentFirstName={profileParticipant?.parent_first_name}
        parentLastName={profileParticipant?.parent_last_name}
        parentEmail={profileParticipant?.parent_email}
        parentPhone={profileParticipant?.parent_phone}
        parentPreferredName={profileParticipant?.parent_preferred_name}
        parentGender={profileParticipant?.parent_gender}
        parentZipCode={profileParticipant?.parent_zip_code}
        parentHighestEducationLevel={profileParticipant?.parent_highest_education_level}
        parentHearAboutUs={profileParticipant?.parent_hear_about_us}
        parentHearAboutUsOther={profileParticipant?.parent_hear_about_us_other}
        createdDate={profileParticipant?.created_date}
      />
    </div>
  );
}

export default EventParticipants;
